import { useTranslation } from 'react-i18next';
import { I18nKey } from '../../../i18n/i18n.key';
import { useTheme } from '../../../themes/theme.provider';
import { useWindow } from '../../../util/window-helper';
import { useTimer } from '../../../hook/timer.hook';
import {
  applyElapsedToTest,
  getClampedElapsedSeconds,
  secondsToHMS,
} from '../../../util/timer.util';
import { memo, useEffect, useRef, useState } from 'react';
import { TimerStage } from '../../../enum/timer-stage.enum';
import { useApp } from '../../../hook/app.hook';
import { TestRes } from '../../../response/test.response';
import { useInvigilate } from '../../../invigilate/invigilate.hook';
import {
  setViolationBlurLock,
  forceClearBlur,
} from '../../../invigilate/invigilate-blur';
import { Modal, Button } from 'react-bootstrap';
import { useSSNavigate } from '../../../navigation/ss-navigate.hook';

const TimerComponent = () => {
  const { isMobile } = useWindow();
  const { t } = useTranslation();
  const { theme, themeType } = useTheme();
  const [timeTaken, setTimeTaken] = useState(0);

  const { testRes, saveResponse, noInternet } = useApp();
  const { stage, startTime, startTimer } = useTimer();
  const { openResult } = useSSNavigate();

  const { fullScreenCloseCount, tabMoveCount } = useInvigilate();

  const intervalId = useRef<any>(null);
  const updateResIntervalId = useRef<any>(null);
  const hasExpiredRef = useRef(false);
  const saveResponseRef = useRef(saveResponse);
  const openResultRef = useRef(openResult);
  const startTimeRef = useRef<number>(
    startTime > 0 ? startTime : new Date().getTime(),
  );
  const testResRef = useRef<TestRes>(testRes);
  const stageRef = useRef(stage);

  saveResponseRef.current = saveResponse;
  openResultRef.current = openResult;

  const saveTest = (submitted: boolean, isStarted: boolean = false) => {
    const _testRes = testResRef.current;
    if (!_testRes?.id) {
      return;
    }

    const elapsed = getClampedElapsedSeconds(
      startTimeRef.current,
      _testRes.timeTaken || 0,
      _testRes.duration,
    );

    saveResponseRef.current(
      {
        ...applyElapsedToTest(_testRes, elapsed),
        isStarted,
      },
      submitted,
      openResultRef.current,
    );
  };

  const saveTestRef = useRef(saveTest);
  saveTestRef.current = saveTest;

  const startSaveRes = () => {
    if (updateResIntervalId.current) {
      clearInterval(updateResIntervalId.current);
    }
    updateResIntervalId.current = setInterval(() => {
      saveTestRef.current(false);
    }, 20000);
  };

  useEffect(() => {
    if (startTime > 0) {
      startTimeRef.current = startTime;
    }
  }, [startTime]);

  useEffect(() => {
    testResRef.current = testRes;
  }, [testRes]);

  useEffect(() => {
    stageRef.current = stage;
  }, [stage]);

  useEffect(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }

    if (stage !== TimerStage.Started) {
      return;
    }

    intervalId.current = setInterval(() => {
      if (stageRef.current !== TimerStage.Started) {
        return;
      }

      const currentTestRes = testResRef.current;
      const elapsed = getClampedElapsedSeconds(
        startTimeRef.current,
        currentTestRes?.timeTaken || 0,
        currentTestRes?.duration,
      );
      const _timeTaken = (currentTestRes?.timeTaken || 0) + elapsed;
      setTimeTaken(_timeTaken);

      if (
        currentTestRes?.duration > 0 &&
        _timeTaken >= currentTestRes.duration &&
        !hasExpiredRef.current
      ) {
        hasExpiredRef.current = true;
        saveTestRef.current(true);
      }
    }, 1000);

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, [stage]);

  useEffect(() => {
    startTimer();
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, []);

  useEffect(() => {
    saveTestRef.current(false, true);
    startSaveRes();
    return () => {
      if (updateResIntervalId.current) {
        clearInterval(updateResIntervalId.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      testRes.schedule.proctoring.forceFullScreen &&
      (tabMoveCount > 0 || fullScreenCloseCount > 0)
    ) {
      setViolationBlurLock(true);
      setShow(true);
    }
  }, [
    tabMoveCount,
    fullScreenCloseCount,
    testRes.schedule.proctoring.forceFullScreen,
  ]);

  const [show, setShow] = useState(false);
  const [showInternetAlert, setShowInternetAlert] = useState(false);

  const handleCloseInternetAlert = () => {
    setShowInternetAlert(false);
  };

  const handleClose = () => {
    setShow(false);
    setViolationBlurLock(false);
    forceClearBlur();

    if (!testRes.schedule.proctoring.forceFullScreen) {
      return;
    }

    const limitCrossed =
      tabMoveCount >= testRes.schedule.proctoring.screenSwitchLimit ||
      fullScreenCloseCount >= testRes.schedule.proctoring.screenSwitchLimit;

    if (limitCrossed) {
      // Limit exceeded on any device (mobile included) -> submit the test.
      saveTestRef.current(true);
    }
    // else if (isMobile) {
    //   // Under the limit on mobile -> reload to re-enter fullscreen.
    //   window.location.reload();
    // }
  };

  // useEffect(() => {
  //   if (noInternet) {
  //     setShowInternetAlert(true);
  //   } else {
  //     setShowInternetAlert(false);
  //   }
  // }, [noInternet]);

  return (
    <>
      <div
        style={{ color: theme.themewhite, minWidth: 204, textAlign: 'left' }}
        className={`${isMobile ? 'ms-auto' : 'mx-3'}`}
      >
        <img
          src={`/images/${
            themeType === 'light' ? 'timer-dark.svg' : 'timer.svg'
          }`}
          alt=""
        />
        <span className="mx-2">
          {t(I18nKey.TEST_TIME)}: {secondsToHMS(testRes.duration - timeTaken)}
        </span>
      </div>

      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton={false}>
          <p className="text-danger">Instruction Violation</p>
        </Modal.Header>
        <Modal.Body>
          Please do not change tab or window Size, Next time we will terminate
          your test!
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              handleClose();
            }}
          >
            Ok
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showInternetAlert}
        onHide={handleCloseInternetAlert}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton={false}>
          <p className="text-danger">No Internet Connection</p>
        </Modal.Header>
        <Modal.Body>
          No internet connection. Please check your internet connection.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              handleCloseInternetAlert();
            }}
          >
            Ok
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default memo(TimerComponent);
